import type {ErrorMap} from 'src/types/redux';
import i18next from 'src/components/lib/i18n/new-i18n';
import type {
  Contact,
  ApiContact,
  ContactFormData,
  SuggestionType,
  VirtualContact,
} from 'src/types/contacts';

import omit from 'lodash/omit';
import pick from 'lodash/pick';
import cloneDeep from 'lodash/cloneDeep';

import {validateIn} from 'src/utils';
import {normalizePhone} from 'src/utils/phone';


export const getPrimaryPhone = (
  contact: Contact,
): string | null | undefined => {
  const primaryPhoneNumber = contact.phoneNumbers.find(({primary}) => primary);
  return primaryPhoneNumber && primaryPhoneNumber.value;
};

// @ts-expect-error - TS7006 - Parameter 'str' implicitly has an 'any' type.
const validString = (str) => Boolean(typeof str === 'string' && str.trim());
// @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
const validatePhone = ({value}) =>
  !validString(value) &&
  i18next.t('Invalid_phone_number', 'Invalid phone number');
const validateEmail = ({address}: {address: string}) =>
  !validString(address) && 'Invalid email address';

// @ts-expect-error - TS7030 - Not all code paths return a value.
const validatePhones = (phones: any) => {
  const errors: Record<string, any> = {};

  if (!phones || phones.length === 0) {
    errors.errors = ['Must provide at least one phone number'];
  } else {
    // @ts-expect-error - TS7006 - Parameter 'phone' implicitly has an 'any' type.
    if (!phones.some((phone) => phone.primary)) {
      errors.errors = ['Must mark one phone number as primary'];
    }

    // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
    phones.forEach((_, index) =>
      // @ts-expect-error - TS2345 - Argument of type '({ value }: { value: any; }) => string | false' is not assignable to parameter of type '(val: unknown, obj?: any) => string | false | Record<any, any> | string[]'.
      Object.assign(errors, validateIn(phones, index, validatePhone)),
    );
  }

  if (Object.keys(errors).length > 0) {
    return errors;
  }
};

// @ts-expect-error - TS7030 - Not all code paths return a value.
const validateEmails = (emails: any) => {
  if (!emails || emails.length === 0) {
    return;
  }

  const errors: Record<string, any> = {};

  const onlyOneEmail = emails.length === 1;
  const singleAddress = emails[0].address;
  const singleAddressIsEmpty =
    !singleAddress || singleAddress.trim().length === 0;
  const noNeedToValidateEmails = onlyOneEmail && singleAddressIsEmpty;

  if (noNeedToValidateEmails) {
    return;
  }

  // @ts-expect-error - TS7006 - Parameter 'email' implicitly has an 'any' type.
  if (!emails.some((email) => email.primary)) {
    errors.errors = ['Must mark one email address as primary'];
  }

  // @ts-expect-error - TS7006 - Parameter '_' implicitly has an 'any' type. | TS7006 - Parameter 'index' implicitly has an 'any' type.
  emails.forEach((_, index) =>
    // @ts-expect-error - TS2345 - Argument of type '({ address }: { address: string; }) => false | "Invalid email address"' is not assignable to parameter of type '(val: unknown, obj?: any) => string | false | Record<any, any> | string[]'.
    Object.assign(errors, validateIn(emails, index, validateEmail)),
  );

  if (Object.keys(errors).length > 0) {
    return errors;
  }
};

export const validateContactForm = (
  formData: ContactFormData,
  // @ts-expect-error - TS7030 - Not all code paths return a value.
): ErrorMap | undefined => {
  const errors: Record<string, any> = {};

  Object.assign(
    errors,
    validateIn(
      formData,
      'firstName',
      (firstName) =>
        // @ts-expect-error - TS2322 - Type 'string | true' is not assignable to type 'string | false | Record<any, any> | string[]'.
        validString(firstName) ||
        `${i18next.t(
          'Must_provide_a_first_name',
          'Must provide a first name',
        )}`,
    ),
  );

  Object.assign(
    errors,
    validateIn(
      formData,
      'lastName',
      (lastName) =>
        // @ts-expect-error - TS2322 - Type 'string | true' is not assignable to type 'string | false | Record<any, any> | string[]'.
        validString(lastName) ||
        `${i18next.t('Must_provide_a_last_name', 'Must provide a last name')}`,
    ),
  );

  // @ts-expect-error - TS2345 - Argument of type '(phones: any) => Record<string, any> | undefined' is not assignable to parameter of type '(val: unknown, obj?: ContactFormData | undefined) => string | false | Record<any, any> | string[]'.
  Object.assign(errors, validateIn(formData, 'phoneNumbers', validatePhones));

  // @ts-expect-error - TS2345 - Argument of type '(emails: any) => Record<string, any> | undefined' is not assignable to parameter of type '(val: unknown, obj?: ContactFormData | undefined) => string | false | Record<any, any> | string[]'.
  Object.assign(errors, validateIn(formData, 'emails', validateEmails));

  if (Object.values(errors).some((field) => field !== true)) {
    return errors;
  }
};

export const pack = (formData: ContactFormData): ApiContact => {
  const sortPrimaryFirst = (a: any, b: any) => {
    if (a.primary) {
      return -1;
    }

    if (b.primary) {
      return 1;
    }

    return 0;
  };

  const contact = cloneDeep(omit(formData, ['audienceMemberId']));

  // @ts-expect-error - TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'?
  contact.phones.sort(sortPrimaryFirst);
  // @ts-expect-error - TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'?
  contact.phone = contact.phones.shift().number;
  // @ts-expect-error - TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'?
  if (contact.phones.length > 0) {
    // @ts-expect-error - TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'? | TS7006 - Parameter 'phone' implicitly has an 'any' type.
    contact.phones.forEach((phone) => {
      delete phone.id;
      delete phone.primary;
    });
    // @ts-expect-error - TS2339 - Property 'additionalPhones' does not exist on type 'Partial<ContactNew | Contact>'. | TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'?
    contact.additionalPhones = contact.phones;
  }

  // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
  if (contact.emails.length > 0) {
    // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
    contact.emails.sort(sortPrimaryFirst);
    // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
    contact.email = contact.emails.shift().address;

    // @ts-expect-error - TS18049 - 'contact.email' is possibly 'null' or 'undefined'.
    if (contact.email.trim().length === 0) {
      delete contact.email;
    }

    // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
    if (contact.emails.length > 0) {
      // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'? | TS7006 - Parameter 'email' implicitly has an 'any' type.
      contact.emails.forEach((email) => {
        delete email.id;
        delete email.primary;
      });
      // @ts-expect-error - TS2339 - Property 'additionalEmails' does not exist on type 'Partial<ContactNew | Contact>'. | TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
      contact.additionalEmails = contact.emails;
    }
  }

  // @ts-expect-error - TS2551 - Property 'phones' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'phone'?
  delete contact.phones;
  // @ts-expect-error - TS2551 - Property 'emails' does not exist on type 'Partial<ContactNew | Contact>'. Did you mean 'email'?
  delete contact.emails;

  // @ts-expect-error - TS2322 - Type 'Partial<ContactNew | Contact>' is not assignable to type 'ApiContact'.
  return contact;
};

export const getVirtualContact = (
  suggestion: SuggestionType | string,
): VirtualContact =>
  // @ts-expect-error - TS2339 - Property 'atsContact' does not exist on type 'string | SuggestionType'.
  suggestion.atsContact ||
  // @ts-expect-error - TS2339 - Property 'contact' does not exist on type 'string | SuggestionType'.
  suggestion.contact ||
  // @ts-expect-error - TS2339 - Property 'audienceMember' does not exist on type 'string | SuggestionType'.
  suggestion.audienceMember ||
  // @ts-expect-error - TS2339 - Property 'threadListSuggestion' does not exist on type 'string | SuggestionType'. | TS2339 - Property 'threadListSuggestion' does not exist on type 'string | SuggestionType'.
  (suggestion.threadListSuggestion && suggestion.threadListSuggestion);

export const getVirtualContactPhoneNumber = (
  contact: SuggestionType | string,
  currentPhoneCountry?: string,
): string | null | undefined => {
  const person = getVirtualContact(contact) || contact;
  return normalizePhone(
    person.phone || person.phoneNumber || person,
    currentPhoneCountry || 'US',
  );
};

export const getVirtualContactPhoneNumberFromToken = (
  token: any,
): string | null | undefined => {
  return (
    token?.sourceObject?.contact?.phone ||
    token?.sourceObject?.contact?.phoneNumber ||
    token?.sourceObject?.audienceMember?.phoneNumber ||
    token?.sourceObject?.atsContact?.phoneNumber ||
    token?.value
  );
};
